import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetJustificationActiveForNsuQuery } from "@/services/justification";
import { formatToBRL } from "@/utils/formatToMoney";
import { ExtendedColumnDef } from "components/Datatable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import React, { useState } from "react";
import { Controller, FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { AlertCircle } from "lucide-react";
import { IAccountConciliation } from "src/@types/deposit-conference";
import { NsuRowActions } from "./components/nsu-row-actions";
import { DisabledCell, NarrowHeader } from "./styles";
import { NsuStatusEnum } from "@/enum/nsu-status";


interface IUseColumnsProps {
  methods: UseFormReturn<FieldValues, any, undefined>;
  requestedNsuIds: string[];
  requestedReceipts?: boolean;
}

export const useColumns = ({
  methods,
  requestedNsuIds = [],
  requestedReceipts = false,
}: IUseColumnsProps) => {
  const { control, setValue } = methods;
  const { data: justifications } = useGetJustificationActiveForNsuQuery();

  const columns: ExtendedColumnDef<IAccountConciliation>[] = [
    {
      accessorKey: "cod",
      header: "Código NSU",
      cell: ({ row }) => {
        const nsu = row.original;
        return (
          <DisabledCell className="w-full p-0" hasDeposit={false}>{nsu?.number}</DisabledCell>
        );
      },
      className: "disabled-column",
    },
    {
      accessorKey: "operator",
      header: "Operador",
      cell: ({ row }) => {
        const nsu = row.original;
        return (
          <div>
            {nsu?.operator}
          </div>
        );
      },
      className: "disabled-column",
    },
    {
      accessorKey: "value",
      header: () => <NarrowHeader className="w-[90px]">Valor</NarrowHeader>,
      cell: ({ row }) => {
        const nsu = row.original;
        const amount = nsu?.amount ?? 0;
        const formattedValue = Math.abs(amount).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        return (
          <div className={`${amount < 0 ? 'text-red-500' : ''}`}>
            {amount < 0 ? `- ${formattedValue}` : formattedValue}
          </div>
        );
      },
      className: "disabled-column",
    },
    {
      id: "adjustment_amount",
      header: () => (
        <NarrowHeader className="w-[70px]">Vlr.<br />Extrato</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;
        const [isEditing, setIsEditing] = useState(false);
        const [editValue, setEditValue] = useState("");
        const fieldName = `nsu_${nsu.id}.adjustment_amount`;

        React.useEffect(() => {
          const currentValue = methods.getValues(fieldName);
          if (nsu.adjustment_amount !== undefined && currentValue === undefined) {
            setValue(fieldName, nsu.adjustment_amount);
          }
        }, [nsu.adjustment_amount, fieldName, setValue]);

        const currentValue = methods.getValues(fieldName) ?? nsu.adjustment_amount;

        const handleStartEdit = () => {
          setEditValue(currentValue !== undefined && currentValue !== null ? currentValue.toString().replace(".", ",") : "");
          setIsEditing(true);
        };

        const handleEdit = () => {
          if (isEditing && editValue) {
            const cleanValue = editValue.replace(/[^\d,\-\.]/g, "").replace(",", ".");
            const normalizedValue = cleanValue.replace(/^-+/, '-');
            let numericValue = parseFloat(normalizedValue) || 0;

            setValue(fieldName, numericValue);
            setIsEditing(false);
            setEditValue("");
          }
        };

        return (
          <div className="flex items-center space-x-2 min-w-[180px] relative group">
            <div className="flex-1 text-center">{currentValue === undefined || currentValue === null ? "-" : formatToBRL(currentValue)}</div>
            {!isEditing ? (
              <button
                type="button"
                onClick={handleStartEdit}
                className="hover:text-[#4B303C] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
              </button>
            ) : (
              <div className="flex items-center space-x-1 absolute right-0 top-0 bg-white shadow-sm border rounded-md p-1 z-10">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => {
                    // Permite números negativos mantendo - e ,
                    const value = e.target.value.replace(/[^\d,\-\.]/g, "");
                    setEditValue(value);
                  }}
                  className="w-28 h-6 text-sm border rounded px-1"
                  placeholder="0,00"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEdit();
                    } else if (e.key === "Escape") {
                      setIsEditing(false);
                      setEditValue("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleEdit}
                  className="text-green-600 hover:text-green-700"
                >
                  ✓
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditValue("");
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "total_amount",
      header: () => (
        <NarrowHeader className="w-[70px]">Vlr.<br />Ajustado</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;
        const fieldName = `nsu_${nsu.id}.adjustment_amount`;
        const formValue = methods.watch(fieldName);
        const extractValue = formValue ?? nsu?.adjustment_amount;
        const amount = Number(nsu?.amount ?? 0);

        // Se não houver valor de extrato ou for 0, mostra "-"
        if (extractValue === undefined || extractValue === null) {
          return (
            <div className="flex align-center justify-center w-full min-w-[180px]">
              -
            </div>
          );
        }

        // O valor ajustado é a diferença entre o valor do extrato e o valor base
        const totalValue = extractValue - amount;

        // Se o valor total for 0, mostra "-"
        if (totalValue === 0) {
          return (
            <div className="flex align-center justify-center w-full min-w-[180px]">
              -
            </div>
          );
        }

        return (
          <div className={`flex align-center justify-center w-full min-w-[180px] ${totalValue < 0 ? 'text-red-500' : ''}`}>
            {extractValue === undefined || extractValue === null ? "-" : (
              <>{totalValue < 0 ? '- ' : ''}{formatToBRL(Math.abs(totalValue))}</>
            )}
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "justification",
      header: () => (
        <NarrowHeader className="w-[90px]">Justificativa</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;
        return (
          <div className="flex align-center justify-center w-full min-w-[200px]">
            <FormProvider {...methods}>
              <form className="w-full">
                <Controller
                  name={`nsu_${nsu.id}.justification_id`}
                  control={control}
                  defaultValue={nsu.justification_id?.toString() ?? ""}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        setValue(`nsu_${nsu.id}.justification_id`, value);
                      }}
                      value={field.value || ""}
                    >
                      <SelectTrigger className="w-full border-none text-sm">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent className="z-[90]">
                        {justifications?.map((item) => (
                          <SelectItem key={item.id} value={item.id.toString()}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </form>
            </FormProvider>
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "nsu_requested",
      header: () => (
        <NarrowHeader className="w-[70px]">NSU<br />Requisitado</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;
        const isRequested =
          row.original.nsu_requested ||
          requestedNsuIds.includes(nsu.id?.toString()) ||
          false;

        const isNsuEmpty = !nsu.number;
        const validated = isRequested && isNsuEmpty;

        return (
          <DisabledCell hasDeposit={validated}>
            <div className="flex align-center justify-center w-full checkbox">
              <Checkbox
                checked={validated}
                className={`${validated ? "box-checked" : ""}`}
              />
            </div>
          </DisabledCell>
        );
      },
      className: "disabled-column",
    },
    {
      id: "receipt_requested",
      header: () => (
        <NarrowHeader className="w-[80px]">Comprovante<br />Requisitado</NarrowHeader>
      ),
      cell: ({ row }) => {
        const validated = row.original.receipt_requested || requestedReceipts;
        return (
          <DisabledCell hasDeposit={validated}>
            <div className="flex align-center justify-center w-full checkbox">
              <Checkbox
                checked={validated}
                className={`${validated ? "box-checked" : ""}`}
              />
            </div>
          </DisabledCell>
        );
      },
      className: "disabled-column",
    },
    {
      id: "conferred",
      header: () => (
        <NarrowHeader className="w-[70px]">Conferido</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;
        const validated = nsu?.number;

        // Verificar se o NSU está pendente ou rejeitado (para perda/desconto em folha)
        const isPendingOrRejected =
          (nsu.is_loss || nsu.is_payroll_discount) &&
          (nsu.status === NsuStatusEnum.PENDING || nsu.status === NsuStatusEnum.REJECTED);

        const isBlocked = validated < 1 || isPendingOrRejected;

        // Mensagem de bloqueio com explicação clara do motivo
        const getBlockReason = () => {
          if (validated < 1) return "NSU sem código não pode ser conferido";

          const tipo = nsu.is_loss ? "perda" : "desconto em folha";

          if (nsu.status === NsuStatusEnum.PENDING) {
            return `Este NSU está marcado como ${tipo} e aguarda aprovação. Só será possível marcar como conferido após a aprovação na tela "Gerenciar Perdas".`;
          }
          if (nsu.status === NsuStatusEnum.REJECTED) {
            return `Este NSU foi rejeitado como ${tipo}. É necessário revisar e corrigir na tela "Gerenciar Perdas" antes de poder conferir.`;
          }
          return "";
        };

        // Inicializar os valores no formulário
        setValue(`nsu_${nsu.id}.id`, nsu.id);
        setValue(`nsu_${nsu.id}.version`, nsu.version);

        const checkboxElement = (
          <FormProvider {...methods}>
            <form>
              <Controller
                name={`nsu_${nsu.id}.conferred`}
                control={control}
                defaultValue={nsu.validated ?? false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    disabled={isBlocked}
                    onCheckedChange={(checked) => {
                      setValue(`nsu_${nsu.id}.conferred`, checked);
                    }}
                    className={`${field.value ? "box-checked" : ""} ${isBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                )}
              />
            </form>
          </FormProvider>
        );

        if (isPendingOrRejected) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`flex items-center justify-center gap-1 w-full checkbox min-w-[120px] cursor-help ${isBlocked ? "no-deposit" : ""}`}
                  >
                    {checkboxElement}
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] bg-amber-50 border-amber-200 text-amber-800">
                  <p className="text-xs">{getBlockReason()}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return (
          <div
            className={`flex align-center justify-center w-full checkbox min-w-[120px] ${isBlocked ? "no-deposit" : ""}`}
          >
            {checkboxElement}
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "is_payroll_discount",
      header: () => (
        <NarrowHeader className="w-[70px]">Desconto<br />em Folha</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;

        // Bloquear alteração se já estiver em análise (PENDING ou REJECTED)
        const isInAnalysis =
          nsu.is_payroll_discount &&
          (nsu.status === NsuStatusEnum.PENDING || nsu.status === NsuStatusEnum.REJECTED);

        const getBlockMessage = () => {
          if (nsu.status === NsuStatusEnum.PENDING) {
            return 'Este NSU está em análise para desconto em folha. Aguarde a aprovação na tela "Gerenciar Perdas".';
          }
          if (nsu.status === NsuStatusEnum.REJECTED) {
            return 'Este NSU foi rejeitado como desconto em folha. Revise na tela "Gerenciar Perdas".';
          }
          return "";
        };

        const checkboxElement = (
          <FormProvider {...methods}>
            <form>
              <Controller
                name={`nsu_${nsu.id}.is_payroll_discount`}
                control={control}
                defaultValue={nsu.is_payroll_discount ?? false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    disabled={isInAnalysis}
                    onCheckedChange={(checked) => {
                      setValue(`nsu_${nsu.id}.is_payroll_discount`, checked);
                    }}
                    className={`${field.value ? "box-checked" : ""} ${isInAnalysis ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                )}
              />
            </form>
          </FormProvider>
        );

        if (isInAnalysis) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center gap-1 w-full checkbox min-w-[120px] cursor-help">
                    {checkboxElement}
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] bg-amber-50 border-amber-200 text-amber-800">
                  <p className="text-xs">{getBlockMessage()}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return (
          <div className="flex align-center justify-center w-full checkbox min-w-[120px]">
            {checkboxElement}
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "is_loss",
      header: () => (
        <NarrowHeader className="w-[70px]">Contabiliza<br />Perda</NarrowHeader>
      ),
      cell: ({ row }) => {
        const nsu = row.original;

        // Bloquear alteração se já estiver em análise (PENDING ou REJECTED)
        const isInAnalysis =
          nsu.is_loss &&
          (nsu.status === NsuStatusEnum.PENDING || nsu.status === NsuStatusEnum.REJECTED);

        const getBlockMessage = () => {
          if (nsu.status === NsuStatusEnum.PENDING) {
            return 'Este NSU está em análise como perda. Aguarde a aprovação na tela "Gerenciar Perdas".';
          }
          if (nsu.status === NsuStatusEnum.REJECTED) {
            return 'Este NSU foi rejeitado como perda. Revise na tela "Gerenciar Perdas".';
          }
          return "";
        };

        const checkboxElement = (
          <FormProvider {...methods}>
            <form>
              <Controller
                name={`nsu_${nsu.id}.is_loss`}
                control={control}
                defaultValue={nsu.is_loss ?? false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    disabled={isInAnalysis}
                    onCheckedChange={(checked) => {
                      setValue(`nsu_${nsu.id}.is_loss`, checked);
                    }}
                    className={`${field.value ? "box-checked" : ""} ${isInAnalysis ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                )}
              />
            </form>
          </FormProvider>
        );

        if (isInAnalysis) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center gap-1 w-full checkbox min-w-[120px] cursor-help">
                    {checkboxElement}
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] bg-amber-50 border-amber-200 text-amber-800">
                  <p className="text-xs">{getBlockMessage()}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return (
          <div className="flex align-center justify-center w-full checkbox min-w-[120px]">
            {checkboxElement}
          </div>
        );
      },
      className: "form-column",
    },

    {
      id: "actions",
      header: () => (
        <div className="flex w-full align-center justify-center">Ações</div>
      ),
      cell: ({ row }) => <NsuRowActions nsu={row.original} />,
      className: "actions-column",
    },
  ];

  return columns;
};
